import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint";

import { getLocaleText } from "shared/boot/i18n";
import {
  formatIntlDate,
  fromCurrentToGivenTimezone,
} from "shared/helpers/date";
import { ApplicationRecord } from "shared/services/spraypaint";

import type ActiveStorageAttachment from "./ActiveStorageAttachment";
import type EmailDomain from "./EmailDomain";
import type MediaReleaseRecipient from "./MediaReleaseRecipient";
import type MediaReleasesContactList from "./MediaReleasesContactList";
import type MediaReleasesContactMailingList from "./MediaReleasesContactMailingList";
import type MediaReleaseTemplate from "./MediaReleaseTemplate";
import type Organisation from "./Organisation";
import type OutreachActivity from "./OutreachActivity";
import type User from "./User";

export type MediaReleaseStatusType =
  | "draft"
  | "error"
  | "scheduled"
  | "sending"
  | "sent";

export interface MediaReleaseStatus {
  backgroundColor: string;
  color: string;
  label: string;
  value: MediaReleaseStatusType;
}

export const getMediaReleaseStatuses = () =>
  <MediaReleaseStatus[]>[
    {
      backgroundColor: "#E6EBEF",
      color: "#707889",
      label: getLocaleText("resources.media_release.statuses.draft"),
      value: "draft",
    },
    {
      backgroundColor: "#D6E4FF",
      color: "#096DD7",
      label: getLocaleText("resources.media_release.statuses.scheduled"),
      value: "scheduled",
    },
    {
      backgroundColor: "#D6E4FF",
      color: "#096DD7",
      label: getLocaleText("resources.media_release.statuses.sending"),
      value: "sending",
    },
    {
      backgroundColor: "#C5F7D0",
      color: "#4BA05D",
      label: getLocaleText("resources.media_release.statuses.sent"),
      value: "sent",
    },
    {
      backgroundColor: "#FFCCC7",
      color: "#CD1323",
      label: getLocaleText("resources.media_release.statuses.error"),
      value: "error",
    },
  ];

const STATUSES: Record<MediaReleaseStatusType, string> =
  getMediaReleaseStatuses().reduce<Record<string, string>>(
    (acc, { value }) => ({
      ...acc,
      [value]: value,
    }),
    {}
  );

export enum MediaReleaseType {
  MediaRelease = "media_release",
  Pitch = "pitch",
}

@Model()
class MediaRelease extends ApplicationRecord {
  static jsonapiType = "media_releases";

  static Status = STATUSES;

  @HasMany() activeStorageAttachments: ActiveStorageAttachment[];

  @Attr({ persist: false }) allRecipientMessagesPersonalised: boolean;

  @Attr({ persist: false }) archivedAt: string;

  @Attr({ persist: false }) createdAt: string;

  @Attr() description: string;

  @Attr({ persist: false }) emailClickedCount: number;

  @Attr({ persist: false }) emailDeliveredCount: number;

  @BelongsTo() emailDomain: EmailDomain;

  @Attr() emailDomainId: number;

  @Attr({ persist: false }) emailFailedCount: number;

  @Attr({ persist: false }) emailOpenedCount: number;

  @Attr() emailRecipients: any[];

  @Attr() fromName: string;

  @HasMany() mediaReleaseRecipients: MediaReleaseRecipient[];

  @HasMany() mediaReleasesContactLists: MediaReleasesContactList[];

  @HasMany()
  mediaReleasesContactMailingLists: MediaReleasesContactMailingList[];

  @BelongsTo() mediaReleaseTemplate: MediaReleaseTemplate;

  @Attr() mediaReleaseTemplateId: number;

  @Attr() mediaReleaseType: MediaReleaseType;

  @Attr() message: string;

  @Attr({ persist: false }) name: string;

  @HasMany() nonListRecipients: MediaReleaseRecipient[];

  @Attr() notifyOnClick: boolean;

  @Attr() notifyOnOpen: boolean;

  @BelongsTo() organisation: Organisation;

  @BelongsTo() organisationBrand: Organisation;

  @Attr() organisationBrandId: number;

  @Attr() organisationId: number;

  @Attr() organisationTeamId: number;

  @HasOne({ name: "activity" }) outreachActivity: OutreachActivity;

  @Attr() pendingDocumentImportId?: number | null;

  @Attr() personalised: boolean;

  @Attr() private: boolean;

  @Attr() recipientCount: number;

  @Attr() recipients: MediaReleaseRecipient[];

  @Attr() replyTo: string;

  @Attr() scheduledAt: string;

  @Attr() senderEmail: string;

  @Attr({ persist: false }) sentAt: string;

  @Attr({ persist: false }) someRecipientMessagesPersonalised: boolean;

  @Attr() status: MediaReleaseStatusType;

  @Attr() subject: string;

  @HasMany({ name: "taggable" }) tags: any[];

  @Attr() title: string;

  @Attr({ persist: false }) unsubscribedRecipientEmails: string[];

  @HasMany() unsubscribedRecipients: MediaReleaseRecipient[];

  @BelongsTo() user: User;

  @Attr() userId: number;

  formattedScheduledAt() {
    return formatIntlDate(this.scheduledAt, {
      dateStyle: "short",
      timeStyle: "medium",
    });
  }

  formattedSentAt() {
    return formatIntlDate(this.sentAt, {
      dateStyle: "short",
      timeStyle: "medium",
    });
  }

  isSent() {
    return this.status === STATUSES.sent;
  }

  scheduledAtExpired(timezone: string) {
    return (
      ![STATUSES.sending, STATUSES.sent, STATUSES.error].includes(
        this.status
      ) &&
      this.scheduledAt &&
      fromCurrentToGivenTimezone(new Date(this.scheduledAt), timezone) <
        fromCurrentToGivenTimezone(new Date(), timezone)
    );
  }

  statusLabel() {
    if (this.isSent()) {
      return getLocaleText("resources.media_release.status_label_sent", {
        date: this.formattedSentAt(),
      });
    }

    if (this.scheduledAt) {
      if (this.status === (STATUSES.draft as MediaReleaseStatusType)) {
        return getLocaleText(
          "resources.media_release.status_label_scheduled_at_draft",
          { date: this.formattedScheduledAt() }
        );
      }

      return getLocaleText(
        "resources.media_release.status_label_scheduled_at",
        { date: this.formattedScheduledAt() }
      );
    }

    return getMediaReleaseStatuses().find(({ value }) => value === this.status)
      ?.label;
  }
}

export default MediaRelease;
